import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Skeleton,
  Alert,
  Grid,
  Input,
  Select,
  Option,
  IconButton,
  FormControl,
  FormLabel,
  Table,
  Sheet,
  Modal,
  ModalDialog,
  ModalClose,
  Chip,
  Snackbar,
  DialogTitle,
  DialogContent,
} from '@mui/joy';
import { Container } from '@mui/material';
import NativeCheckbox from '../../main/components/NativeCheckbox';
import PaymentsIcon from '@mui/icons-material/Payments';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DirectionalBackIcon from '../../main/components/DirectionalBackIcon';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useAuth } from '../../main/context/AuthContext';
import { getCustomerPayments, exportCustomerPaymentsToExcel } from '../../main/API/customer';
import { useNavigate } from 'react-router-dom';
import { formatPaymentStatus, getPaymentStatusChipStyle } from '../../main/widgets/PaymentStatusOptionList';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentReceipt from '../../shipments_manager/views/payment_receipt_screen';
import { OmrAmount } from '../../main/components/OmrAmount';
import { translateApiErrorData } from '../../main/utils/masterApiError';
import { dedupeRequest } from '../../main/utils/dedupeRequest';
import { getPaginatedMeta } from '../../main/utils/pagination';
import {
  formatCustomerShipmentUnitDate,
  getCustomerShipmentUnit,
  getCustomerShipmentUnitSerial,
  isCustomerShipmentUnitFieldVisible,
  useCustomerShipmentUnitVisibleFields,
} from '../utils/customerShipmentUnit';

function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (_) {
    return '—';
  }
}

function formatDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return '—';
  }
}

// Distinct colors per payment method (same soft-chip style as invoice Payment column, no border)
function getPaymentMethodChipStyle(value) {
  const styles = {
    Cash: { bgcolor: '#dcfce7', color: '#166534' },
    Transfer: { bgcolor: '#dbeafe', color: '#1e40af' },
    Online: { bgcolor: '#e0e7ff', color: '#3730a3' },
    Cheque: { bgcolor: '#fef3c7', color: '#b45309' },
    Paymob: { bgcolor: '#fce7f3', color: '#9d174d' },
    Thawani: { bgcolor: '#f3e8ff', color: '#6b21a8' },
  };
  return value && styles[value]
    ? styles[value]
    : { bgcolor: '#f1f5f9', color: '#475569' };
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const initialFilters = {
  payment_method: '',
  transaction_date: '',
  transaction_date_start: '',
  transaction_date_end: '',
  start_date: '',
  end_date: '',
  ordering: '-created_at',
};

function toIsoDateOnly(dateStr) {
  if (!dateStr || !String(dateStr).trim()) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function parseRefundInfo(payment) {
  const amount = parseFloat(payment?.amount || 0);
  const refunded = parseFloat(payment?.refunded_amount || 0);
  const isRefunded = payment?.is_refunded === true;
  const net = amount - refunded;
  const isPartial = refunded > 0 && refunded < amount;
  return { amount, refunded, net, isRefunded, isPartial };
}

export default function CustomerMyPaymentsScreen() {
  const { t, i18n } = useTranslation('ui');
  const { user, logout } = useAuth();
  const tRef = useRef(t);
  const logoutRef = useRef(logout);
  tRef.current = t;
  logoutRef.current = logout;
  const accountInactive = Boolean(user && user.is_active === false);
  const unitVisibleFields = useCustomerShipmentUnitVisibleFields();
  const navigate = useNavigate();

  const orderingOptions = useMemo(
    () => [
      { value: '-created_at', label: t('customerList.orderNewestFirst') },
      { value: 'created_at', label: t('customerList.orderOldestFirst') },
      { value: '-transaction_date', label: t('customerList.orderTxnNewest') },
      { value: 'transaction_date', label: t('customerList.orderTxnOldest') },
      { value: '-amount', label: t('customerList.orderAmountHighToLow') },
      { value: 'amount', label: t('customerList.orderAmountLowToHigh') },
      { value: '-id', label: t('customerList.orderIdHighToLow') },
      { value: 'id', label: t('customerList.orderIdLowToHigh') },
      { value: 'payment_method', label: t('customerList.orderPaymentMethodAZ') },
      { value: '-payment_method', label: t('customerList.orderPaymentMethodZA') },
    ],
    [t]
  );

  const paymentMethodOptionsWithAll = useMemo(
    () => [
      { value: '', label: t('customerList.allPaymentMethods') },
      { value: 'Cash', label: t('customerList.pmCash') },
      { value: 'Transfer', label: t('customerList.pmTransfer') },
      { value: 'Online', label: t('customerList.pmOnline') },
      { value: 'Cheque', label: t('customerList.pmCheque') },
      { value: 'Paymob', label: t('customerList.pmPaymob') },
      { value: 'Thawani', label: t('customerList.pmThawani') },
    ],
    [t]
  );

  const paymentMethodLabel = useCallback(
    (value) => {
      if (!value) return '—';
      const opt = paymentMethodOptionsWithAll.find((o) => o.value === value);
      return opt ? opt.label : value;
    },
    [paymentMethodOptionsWithAll]
  );
  const [data, setData] = useState({ results: [], count: 0, page_index: 1, page_size: 10 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [quickViewPayment, setQuickViewPayment] = useState(null);
  const [receiptPdfPayment, setReceiptPdfPayment] = useState(null);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState(new Set());
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportSuccessMessage, setExportSuccessMessage] = useState(null);

  const buildParams = useCallback(() => {
    const p = { page, page_size: pageSize, ordering: appliedFilters.ordering };
    [
      'payment_method', 'transaction_date', 'transaction_date_start', 'transaction_date_end',
      'start_date', 'end_date',
    ].forEach((key) => {
      const v = appliedFilters[key];
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        const dateVal = ['transaction_date', 'transaction_date_start', 'transaction_date_end', 'start_date', 'end_date'].includes(key)
          ? toIsoDateOnly(v)
          : v;
        if (dateVal) p[key] = dateVal;
      }
    });
    return p;
  }, [page, pageSize, appliedFilters]);

  const listRequestKey = `customer/payments:${page}:${pageSize}:${JSON.stringify(appliedFilters)}`;

  const fetchPayments = useCallback(async ({ dedupeKey, isCancelled } = {}) => {
    if (isCancelled && isCancelled()) return;
    if (accountInactive) {
      setLoading(false);
      setData({ results: [], count: 0, page_index: 1, page_size: pageSize });
      setError(null);
      return;
    }
    setError(null);
    setLoading(true);
    const params = buildParams();
    const doFetch = () => getCustomerPayments(params);
    const response = dedupeKey
      ? await dedupeRequest(dedupeKey, doFetch)
      : await doFetch();
    if (isCancelled && isCancelled()) return;
    setLoading(false);

    if (!response) {
      setError(tRef.current('customerList.loadPaymentsError'));
      return;
    }
    if (response.status === 401) {
      logoutRef.current();
      return;
    }
    if (response.status >= 400) {
      setError((response.data && translateApiErrorData(response.data, tRef.current)) || tRef.current('customerList.loadPaymentsFailed'));
      setData({ results: [], count: 0, page_index: 1, page_size: pageSize });
      return;
    }

    const body = response.data;
    setData(getPaginatedMeta(body, pageSize));
  }, [accountInactive, buildParams, pageSize]);

  const fetchPaymentsRef = useRef(fetchPayments);
  fetchPaymentsRef.current = fetchPayments;

  useEffect(() => {
    let cancelled = false;
    fetchPaymentsRef.current({ dedupeKey: listRequestKey, isCancelled: () => cancelled });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRequestKey, accountInactive]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPage(1);
  };

  const hasActiveFilters = Object.keys(initialFilters).some(
    (k) => k !== 'ordering' && appliedFilters[k] !== undefined && appliedFilters[k] !== null && String(appliedFilters[k]).trim() !== ''
  );
  const clearAllFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(1);
  };

  const pageIds = (data.results || []).map((p) => p.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedPaymentIds.has(id));
  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedPaymentIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedPaymentIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };
  const toggleSelectPayment = (id) => {
    setSelectedPaymentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExportToExcel = async () => {
    setExportError(null);
    setExportLoading(true);
    try {
      const hasSelection = selectedPaymentIds.size > 0;
      const params = hasSelection
        ? { payment_ids: Array.from(selectedPaymentIds) }
        : (() => {
            const p = { ordering: appliedFilters.ordering };
            ['payment_method', 'transaction_date', 'transaction_date_start', 'transaction_date_end', 'start_date', 'end_date'].forEach((key) => {
              const v = appliedFilters[key];
              if (v !== undefined && v !== null && String(v).trim() !== '') {
                const dateVal = ['transaction_date', 'transaction_date_start', 'transaction_date_end', 'start_date', 'end_date'].includes(key)
                  ? toIsoDateOnly(v)
                  : v;
                if (dateVal) p[key] = dateVal;
              }
            });
            return p;
          })();
      const response = await exportCustomerPaymentsToExcel(params);
      setExportLoading(false);
      if (!response) {
        setExportError(t('customerList.exportFailed'));
        return;
      }
      if (response.status === 401) {
        logout();
        return;
      }
      if (response.status >= 400) {
        let msg = t('customerList.exportFailedShort');
        if (response.data instanceof Blob) {
          try {
            const text = await response.data.text();
            const json = JSON.parse(text);
            msg = translateApiErrorData(json, t) || msg;
          } catch (_) {
            // keep default msg
          }
        } else if (response.data) {
          msg = translateApiErrorData(response.data, t) || msg;
        }
        setExportError(msg);
        return;
      }
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_');
      link.download = `my_payments_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      setExportError(null);
      setExportSuccessMessage(t('customerList.exportSuccess'));
    } catch (err) {
      setExportLoading(false);
      setExportError(err.message || t('customerList.exportFailedShort'));
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.count || 0) / pageSize));
  const pageBg = '#f0f4f8';
  const cardBg = '#ffffff';
  const cardBorder = '1px solid #e2e8f0';
  const cardShadow = '0 2px 8px rgba(0,0,0,0.06)';
  const cardRadius = '16px';
  const textPrimary = '#1e293b';
  const textSecondary = '#64748b';
  const textMuted = '#94a3b8';

  if (loading && !data.results.length) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
        <Box component="main" sx={{ overflow: 'auto', pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Container maxWidth="xl">
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: cardRadius, mb: 3, bgcolor: cardBg }} />
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: cardRadius, mb: 2, bgcolor: cardBg }} />
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((i) => (
                <Grid key={i} xs={12} sm={6} md={4}>
                  <Skeleton variant="rectangular" height={180} sx={{ borderRadius: cardRadius, bgcolor: cardBg }} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
      <Box component="main" sx={{ overflow: 'auto', pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="plain"
                size="sm"
                startDecorator={<DirectionalBackIcon />}
                onClick={() => navigate('/customer-home')}
                sx={{ color: textSecondary, fontWeight: 500, mr: 0.5 }}
              >
                {t('customerList.homeNav')}
              </Button>
              <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PaymentsIcon sx={{ color: '#ea580c', fontSize: 22 }} />
              </Box>
              <Typography level="h4" sx={{ fontWeight: 700, color: textPrimary }}>
                {t('customerList.myPaymentsTitle')}
              </Typography>
            </Box>
            <Button
              variant="soft"
              size="sm"
              startDecorator={<RefreshIcon />}
              onClick={fetchPayments}
              loading={loading}
              sx={{ borderRadius: '10px', fontWeight: 500 }}
            >
              {t('actions.refresh')}
            </Button>
          </Box>

          {error && (
            <Alert
              color="danger"
              variant="soft"
              sx={{ mb: 3, borderRadius: '12px' }}
              endDecorator={
                <Button variant="soft" color="danger" size="sm" onClick={fetchPayments}>
                  {t('actions.retry')}
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          <Card variant="plain" sx={{ bgcolor: cardBg, boxShadow: cardShadow, borderRadius: cardRadius, border: cardBorder, mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', mb: showAllFilters ? 2 : 0 }}>
                <Select
                  size="md"
                  value={filters.payment_method}
                  onChange={(_, v) => updateFilter('payment_method', v || '')}
                  sx={{ minWidth: 160, borderRadius: '10px' }}
                >
                  {paymentMethodOptionsWithAll.map((opt) => (
                    <Option key={opt.value || 'all'} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
                <Select
                  size="md"
                  value={filters.ordering}
                  onChange={(_, v) => updateFilter('ordering', v || '-created_at')}
                  sx={{ minWidth: 160, borderRadius: '10px' }}
                >
                  {orderingOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
                <Button size="md" onClick={handleApplyFilters} sx={{ borderRadius: '10px' }}>
                  {t('actions.apply')}
                </Button>
                {hasActiveFilters && (
                  <Button variant="plain" size="md" onClick={clearAllFilters}>
                    {t('customerList.clearAll')}
                  </Button>
                )}
                <Button
                  variant="soft"
                  size="md"
                  color="neutral"
                  onClick={() => setShowAllFilters((s) => !s)}
                  endDecorator={showAllFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  startDecorator={<FilterListIcon />}
                  sx={{ borderRadius: '10px' }}
                >
                  {showAllFilters ? t('customerList.lessFilters') : t('customerList.allFilters')}
                </Button>
                <Button
                  variant="soft"
                  size="md"
                  startDecorator={<FileDownloadIcon />}
                  onClick={handleExportToExcel}
                  loading={exportLoading}
                  disabled={exportLoading}
                  sx={{ borderRadius: '10px', fontWeight: 500 }}
                >
                  {selectedPaymentIds.size > 0
                    ? t('customerList.exportExcelSelected', { count: selectedPaymentIds.size })
                    : t('customerList.exportExcel')}
                </Button>
              </Box>

              {showAllFilters && (
                <Box sx={{ pt: 2, borderTop: '1px solid #e2e8f0' }}>
                  <Grid container spacing={2}>
                    <Grid xs={12}>
                      <Typography level="title-sm" sx={{ color: textSecondary, mb: 1 }}>{t('customerList.paymentMethodDatesSection')}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('widgets.paymentMethod')}</FormLabel>
                        <Select
                          value={filters.payment_method}
                          onChange={(_, v) => updateFilter('payment_method', v || '')}
                          sx={{ borderRadius: '8px' }}
                        >
                          {paymentMethodOptionsWithAll.map((opt) => (
                            <Option key={opt.value || 'all'} value={opt.value}>{opt.label}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.transactionDateExact')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.transaction_date}
                          onChange={(e) => updateFilter('transaction_date', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.transactionDateFrom')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.transaction_date_start}
                          onChange={(e) => updateFilter('transaction_date_start', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.transactionDateTo')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.transaction_date_end}
                          onChange={(e) => updateFilter('transaction_date_end', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.createdFromLabel')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.start_date}
                          onChange={(e) => updateFilter('start_date', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.createdToLabel')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.end_date}
                          onChange={(e) => updateFilter('end_date', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button size="sm" onClick={handleApplyFilters}>
                      {t('customerList.applyFilters')}
                    </Button>
                    <Button variant="plain" size="sm" onClick={clearAllFilters}>
                      {t('customerList.clearAll')}
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {selectedPaymentIds.size > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                mb: 2,
                p: 1.5,
                borderRadius: '12px',
                bgcolor: '#ecfdf5',
                border: '1px solid #a7f3d0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography level="body1" sx={{ fontWeight: 600, color: textPrimary }}>
                  {t('customerList.paymentsSelected', { count: selectedPaymentIds.size })}
                </Typography>
                <Button
                  variant="plain"
                  size="sm"
                  onClick={() => setSelectedPaymentIds(new Set())}
                  sx={{ fontWeight: 500 }}
                >
                  {t('customerList.clearSelection')}
                </Button>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography level="body2" sx={{ color: textSecondary }}>
              {data.count === 0 ? t('customerList.noPayments') : t('customerList.totalPayments', { count: data.count })}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="body2" sx={{ color: textSecondary }}>{t('customerList.perPage')}</Typography>
              <Select
                size="sm"
                value={pageSize}
                onChange={(_, v) => {
                  setPageSize(v || 10);
                  setPage(1);
                }}
                sx={{ width: 70 }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <Option key={n} value={n}>{n}</Option>
                ))}
              </Select>
            </Box>
          </Box>

          {!data.results.length ? (
            <Card variant="plain" sx={{ bgcolor: cardBg, borderRadius: cardRadius, boxShadow: cardShadow, border: cardBorder }}>
              <CardContent sx={{ py: 6, px: 2, textAlign: 'center' }}>
                <PaymentsIcon sx={{ fontSize: 48, color: textMuted, opacity: 0.6, mb: 1.5 }} />
                <Typography level="body1" sx={{ color: textSecondary }}>
                  {t('customerList.noPaymentsFound')}
                </Typography>
                {hasActiveFilters && (
                  <Button variant="soft" size="sm" sx={{ mt: 2 }} onClick={clearAllFilters}>
                    {t('customerList.clearFiltersBtn')}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              <Sheet
                variant="outlined"
                sx={{
                  borderRadius: cardRadius,
                  border: cardBorder,
                  boxShadow: cardShadow,
                  overflow: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  '& thead th': { fontWeight: 600, color: textSecondary, fontSize: '0.8125rem', whiteSpace: 'nowrap' },
                  '& tbody tr:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                  '& td': { verticalAlign: 'middle', fontSize: '0.875rem' },
                  '& thead th:first-of-type': {
                    position: 'sticky',
                    top: 0,
                    insetInlineStart: 0,
                    zIndex: 12,
                    minWidth: 48,
                    width: 48,
                    backgroundColor: cardBg,
                    backgroundClip: 'padding-box',
                    boxShadow: '4px 0 8px rgba(0,0,0,0.08)',
                    pointerEvents: 'auto',
                    isolation: 'isolate',
                  },
                  'html[dir="rtl"] & thead th:first-of-type': {
                    boxShadow: '-4px 0 8px rgba(0,0,0,0.08)',
                  },
                  '& tbody td:first-of-type': {
                    position: 'sticky',
                    insetInlineStart: 0,
                    zIndex: 11,
                    backgroundColor: cardBg,
                    backgroundClip: 'padding-box',
                    boxShadow: '4px 0 8px rgba(0,0,0,0.06)',
                    pointerEvents: 'auto',
                    isolation: 'isolate',
                  },
                  'html[dir="rtl"] & tbody td:first-of-type': {
                    boxShadow: '-4px 0 8px rgba(0,0,0,0.06)',
                  },
                  '& tbody tr:hover td:first-of-type': {
                    backgroundColor: 'rgba(0,0,0,0.04)',
                  },
                }}
              >
                <Table stickyHeader hoverRow size="md" sx={{ minWidth: 1248, borderCollapse: 'separate', borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 48 }}>
                        {data.results.length > 0 && (
                          <NativeCheckbox
                            checked={allOnPageSelected}
                            indeterminate={selectedPaymentIds.size > 0 && !allOnPageSelected}
                            onChange={toggleSelectAll}
                            sx={{ verticalAlign: 'middle' }}
                          />
                        )}
                      </th>
                      <th style={{ minWidth: 72, width: 72 }}>{t('customerList.colId')}</th>
                      <th style={{ minWidth: 130 }}>{t('customerList.colMethod')}</th>
                      <th style={{ minWidth: 140 }}>{t('customerList.colAmount')}</th>
                      <th style={{ minWidth: 110 }}>{t('paymentsScreen.colRefunded')}</th>
                      <th style={{ minWidth: 120 }}>{t('paymentsScreen.colRefundStatus')}</th>
                      <th style={{ minWidth: 120 }}>{t('customerList.colTransactionDate')}</th>
                      <th style={{ minWidth: 120 }}>{t('customerList.colCreated')}</th>
                      <th style={{ minWidth: 100 }}>{t('customerList.colInvoice')}</th>
                      <th style={{ minWidth: 120 }}>{t('customerList.colShipments')}</th>
                      <th style={{ minWidth: 120, width: 120 }}>{t('customerList.colAction')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.results.map((p) => {
                      const { amount, refunded, net, isRefunded, isPartial } = parseRefundInfo(p);
                      return (
                      <tr key={p.id}>
                        <td>
                          <NativeCheckbox
                            checked={selectedPaymentIds.has(p.id)}
                            onChange={() => toggleSelectPayment(p.id)}
                            sx={{ verticalAlign: 'middle' }}
                          />
                        </td>
                        <td>
                          <Typography level="body2" sx={{ fontWeight: 600, color: textPrimary }}>
                            #{p.id}
                          </Typography>
                        </td>
                        <td>
                          <Chip
                            size="sm"
                            variant="soft"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              textTransform: 'none',
                              ...getPaymentMethodChipStyle(p.payment_method),
                            }}
                          >
                            {paymentMethodLabel(p.payment_method)}
                          </Chip>
                        </td>
                        <td>
                          <Box>
                            <Typography level="body2" sx={{ fontWeight: 600, color: textPrimary, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                              <OmrAmount value={net} />
                            </Typography>
                            {isRefunded && (
                              <Typography level="body-xs" sx={{ mt: 0.25, color: textSecondary, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <OmrAmount value={amount} />
                                <span aria-hidden>−</span>
                                <OmrAmount value={refunded} />
                                <span>{t('paymentsScreen.amountNetBreakdownSuffix')}</span>
                              </Typography>
                            )}
                          </Box>
                        </td>
                        <td>
                          {refunded > 0 ? (
                            <Typography level="body2" sx={{ color: '#dc2626', fontWeight: 500 }}>
                              <OmrAmount value={refunded} />
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {isRefunded ? (
                            <Chip
                              size="sm"
                              variant="soft"
                              color="warning"
                              sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                            >
                              {isPartial ? t('paymentsScreen.refundPartialLabel') : t('paymentsScreen.refundFullLabel')}
                            </Chip>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>{formatDate(p.transaction_date)}</td>
                        <td>{formatDate(p.created_at)}</td>
                        <td>
                          {p.invoice != null ? (
                            <Typography level="body2" sx={{ fontWeight: 500 }}>#{p.invoice.id}</Typography>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {p.shipments && p.shipments.length > 0 ? (
                            <Typography level="body2" sx={{ color: textSecondary }}>
                              {p.shipments.map((s) => `#${s.id}`).join(', ')}
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                            <IconButton
                              variant="soft"
                              size="sm"
                              color="neutral"
                              onClick={() => setQuickViewPayment(p)}
                              title={t('customer.quickViewButtonTitle')}
                              sx={{ borderRadius: '8px' }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              variant="soft"
                              size="sm"
                              color="neutral"
                              onClick={() => setReceiptPdfPayment(p)}
                              title={t('customer.quickViewReceiptPdfTitle')}
                              sx={{ borderRadius: '8px' }}
                            >
                              <PictureAsPdfIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Sheet>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
                <Button
                  variant="outlined"
                  size="sm"
                  startDecorator={i18n.dir() === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  sx={{ borderRadius: '8px' }}
                >
                  {t('customerList.paginationPrevious')}
                </Button>
                <Typography level="body2" sx={{ color: textSecondary, minWidth: 120, textAlign: 'center' }}>
                  {t('customerList.pageOf', { page, total: totalPages })}
                </Typography>
                <Button
                  variant="outlined"
                  size="sm"
                  endDecorator={i18n.dir() === 'rtl' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  sx={{ borderRadius: '8px' }}
                >
                  {t('customerList.paginationNext')}
                </Button>
              </Box>

              <Modal open={!!quickViewPayment} onClose={() => setQuickViewPayment(null)}>
                <ModalDialog
                  sx={{
                    maxWidth: 600,
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    borderRadius: cardRadius,
                    p: 2.5,
                  }}
                >
                  <ModalClose />
                  {quickViewPayment && (() => {
                    const { amount, refunded, net, isRefunded, isPartial } = parseRefundInfo(quickViewPayment);
                    return (
                    <>
                      <Typography level="h5" sx={{ mb: 2, fontWeight: 700 }}>
                        {t('customerList.paymentNumber', { id: quickViewPayment.id })}
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        {[
                          {
                              label: t('widgets.paymentMethod'),
                              value: (
                                <Chip
                                  size="sm"
                                  variant="soft"
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    ...getPaymentMethodChipStyle(quickViewPayment.payment_method),
                                  }}
                                >
                                  {paymentMethodLabel(quickViewPayment.payment_method)}
                                </Chip>
                              ),
                            },
                          {
                            label: t('actions.amount'),
                            value: (
                              <Box>
                                <Typography level="body1" sx={{ color: textPrimary, fontWeight: 600 }}>
                                  <OmrAmount value={net} />
                                </Typography>
                                {isRefunded && (
                                  <Typography level="body-xs" sx={{ mt: 0.25, color: textSecondary, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    <OmrAmount value={amount} />
                                    <span aria-hidden>−</span>
                                    <OmrAmount value={refunded} />
                                    <span>{t('paymentsScreen.amountNetBreakdownSuffix')}</span>
                                  </Typography>
                                )}
                              </Box>
                            ),
                          },
                          isRefunded ? {
                            label: t('paymentsScreen.colRefunded'),
                            value: (
                              <Typography level="body1" sx={{ color: '#dc2626', fontWeight: 600 }}>
                                <OmrAmount value={refunded} />
                              </Typography>
                            ),
                          } : null,
                          isRefunded ? {
                            label: t('paymentsScreen.colRefundStatus'),
                            value: (
                              <Chip
                                size="sm"
                                variant="soft"
                                color="warning"
                                sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                              >
                                {isPartial ? t('paymentsScreen.refundPartialLabel') : t('paymentsScreen.refundFullLabel')}
                              </Chip>
                            ),
                          } : null,
                          { label: t('customerList.colTransactionDate'), value: formatDate(quickViewPayment.transaction_date) },
                          { label: t('customerList.createdAt'), value: formatDateTime(quickViewPayment.created_at) },
                          { label: t('customerList.colInvoice'), value: quickViewPayment.invoice != null ? `#${quickViewPayment.invoice.id}` : '—' },
                        ].filter(Boolean).map(({ label, value }) => (
                          <Grid key={label} xs={12} sm={6}>
                            <Typography level="body2" sx={{ color: textSecondary, fontWeight: 600, mb: 0.25 }}>
                              {label}
                            </Typography>
                            <Typography level="body1" sx={{ color: textPrimary }}>{value}</Typography>
                          </Grid>
                        ))}
                      </Grid>

                      {quickViewPayment.invoice != null && (
                        <Box sx={{ mt: 2, p: 2, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                          <Typography level="title-md" sx={{ color: textPrimary, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                            <ReceiptIcon fontSize="small" />
                            {t('customerList.invoiceTitleShort', { id: quickViewPayment.invoice.id })}
                          </Typography>
                          <Grid container spacing={1.5}>
                            {quickViewPayment.invoice.note != null && quickViewPayment.invoice.note !== '' && (
                              <Grid xs={12}>
                                <Typography level="body2" sx={{ color: textSecondary }}><strong>{t('customerList.noteBold')}</strong> {quickViewPayment.invoice.note}</Typography>
                              </Grid>
                            )}
                            <Grid xs={12} sm={6}>
                              <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>{t('widgets.paymentStatus')}</Typography>
                              <Chip size="sm" variant="soft" sx={{ mt: 0.25, fontWeight: 500, textTransform: 'none', ...getPaymentStatusChipStyle(quickViewPayment.invoice.payment_status) }}>
                                {formatPaymentStatus(quickViewPayment.invoice.payment_status)}
                              </Chip>
                            </Grid>
                            <Grid xs={12} sm={6}>
                              <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>{t('customerList.paidLabel')}</Typography>
                              <Typography level="body2" sx={{ color: textPrimary }}>{quickViewPayment.invoice.paid ? t('actions.yes') : t('actions.no')}</Typography>
                            </Grid>
                            <Grid xs={12} sm={6}>
                              <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>{t('customer.quickView.created')}</Typography>
                              <Typography level="body2" sx={{ color: textPrimary }}>{formatDateTime(quickViewPayment.invoice.created_at)}</Typography>
                            </Grid>
                            <Grid xs={12} sm={6}>
                              <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>{t('customer.quickView.modified')}</Typography>
                              <Typography level="body2" sx={{ color: textPrimary }}>{formatDateTime(quickViewPayment.invoice.modified_at)}</Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      {quickViewPayment.shipments && quickViewPayment.shipments.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography level="title-md" sx={{ color: textPrimary, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                            <LocalShippingIcon fontSize="small" />
                            {t('customerList.relatedShipmentsTitle', { count: quickViewPayment.shipments.length })}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {quickViewPayment.shipments.map((s) => (
                              <Box
                                key={s.id}
                                sx={{
                                  p: 2,
                                  borderRadius: '12px',
                                  bgcolor: '#f8fafc',
                                  border: '1px solid #e2e8f0',
                                }}
                              >
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography level="title-sm" sx={{ color: textPrimary, fontWeight: 600 }}>{t('customer.shipmentNumber', { id: s.id })}</Typography>
                                  {s.tracking_number ? (
                                    <Chip size="sm" variant="soft" startDecorator={<LocalShippingIcon sx={{ fontSize: 14 }} />}>
                                      {s.tracking_number}
                                    </Chip>
                                  ) : null}
                                  {isCustomerShipmentUnitFieldVisible('serial_number', unitVisibleFields) && getCustomerShipmentUnitSerial(s) ? (
                                    <Chip size="sm" variant="outlined" sx={{ fontSize: '0.75rem' }}>
                                      {t('customer.unit')} {getCustomerShipmentUnitSerial(s)}
                                    </Chip>
                                  ) : null}
                                  {isCustomerShipmentUnitFieldVisible('expected_warehouse_eta', unitVisibleFields) && getCustomerShipmentUnit(s)?.expected_warehouse_eta ? (
                                    <Chip size="sm" variant="outlined" sx={{ fontSize: '0.75rem' }}>
                                      {t('customer.quickView.expectedWarehouseEta')}: {formatCustomerShipmentUnitDate(getCustomerShipmentUnit(s).expected_warehouse_eta)}
                                    </Chip>
                                  ) : null}
                                  {s.shipper_reference ? (
                                    <Typography level="body2" sx={{ color: textSecondary }}>{t('customer.ref')} {s.shipper_reference}</Typography>
                                  ) : null}
                                  {s.status ? (
                                    <Chip size="sm" variant="outlined" sx={{ textTransform: 'capitalize', fontSize: '0.75rem' }}>{String(s.status).replace(/_/g, ' ')}</Chip>
                                  ) : null}
                                  {s.payment_status ? (
                                    <Chip size="sm" variant="soft" sx={{ fontWeight: 500, textTransform: 'none', ...getPaymentStatusChipStyle(s.payment_status) }}>
                                      {formatPaymentStatus(s.payment_status)}
                                    </Chip>
                                  ) : null}
                                </Box>
                                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                  {s.items_description && (
                                    <Grid xs={12}>
                                      <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>{t('customerList.itemsLabel')}</Typography>
                                      <Typography level="body2" sx={{ color: textPrimary }}>{s.items_description}</Typography>
                                    </Grid>
                                  )}
                                  {(s.packages_quantity != null || s.weight != null) && (
                                    <>
                                      {s.packages_quantity != null && (
                                        <Grid xs={6} sm={4}>
                                          <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>{t('customerList.packagesLabel')}</Typography>
                                          <Typography level="body2" sx={{ color: textPrimary }}>{s.packages_quantity}</Typography>
                                        </Grid>
                                      )}
                                      {s.weight != null && (
                                        <Grid xs={6} sm={4}>
                                          <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>{t('customer.quickView.weight')}</Typography>
                                          <Typography level="body2" sx={{ color: textPrimary }}>{s.weight}</Typography>
                                        </Grid>
                                      )}
                                    </>
                                  )}
                                  {s.volumetric_weight != null && (
                                    <Grid xs={6} sm={4}>
                                      <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>{t('customerList.volWeight')}</Typography>
                                      <Typography level="body2" sx={{ color: textPrimary }}>{s.volumetric_weight}</Typography>
                                    </Grid>
                                  )}
                                  {s.price != null && s.price !== '' && (
                                    <Grid xs={12} sm={6}>
                                      <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>{t('customer.quickView.price')}</Typography>
                                      <Typography level="body2" sx={{ color: textPrimary, fontWeight: 600 }}><OmrAmount value={s.price} /></Typography>
                                    </Grid>
                                  )}
                                  {(s.current_location || s.location_name) && (
                                    <Grid xs={12} sm={6}>
                                      <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>{t('customer.quickView.location')}</Typography>
                                      <Typography level="body2" sx={{ color: textPrimary }}>{s.current_location || s.location_name || '—'}</Typography>
                                    </Grid>
                                  )}
                                  {s.note && (
                                    <Grid xs={12}>
                                      <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>{t('customer.quickView.note')}</Typography>
                                      <Typography level="body2" sx={{ color: textPrimary }}>{s.note}</Typography>
                                    </Grid>
                                  )}
                                  {s.receiver_details && (
                                    <Grid xs={12}>
                                      <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem', mb: 0.5 }}>{t('customerList.receiverSection')}</Typography>
                                      <Box sx={{ pl: 0.5 }}>
                                        {s.receiver_details.receiver_name && (
                                          <Typography level="body2" sx={{ color: textPrimary }}>{t('customerList.namePrefix')} {s.receiver_details.receiver_name}</Typography>
                                        )}
                                        {s.receiver_details.receiver_phone_number && (
                                          <Typography level="body2" sx={{ color: textPrimary }}>{t('customerList.phonePrefix')} {s.receiver_details.receiver_phone_number}</Typography>
                                        )}
                                        {s.receiver_details.receiver_civil_id && (
                                          <Typography level="body2" sx={{ color: textPrimary }}>{t('customerList.civilIdPrefix')} {s.receiver_details.receiver_civil_id}</Typography>
                                        )}
                                        {s.receiver_details.destination_location_type && (
                                          <Typography level="body2" sx={{ color: textPrimary }}>{t('customerList.destinationTypePrefix')} {String(s.receiver_details.destination_location_type).replace(/_/g, ' ')}</Typography>
                                        )}
                                        {s.receiver_details.destination_location && (
                                          <Typography level="body2" sx={{ color: textPrimary }}>{t('customerList.addressPrefix')} {s.receiver_details.destination_location}</Typography>
                                        )}
                                      </Box>
                                    </Grid>
                                  )}
                                  <Grid xs={12} sm={6}>
                                    <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>{t('customer.quickView.created')}</Typography>
                                    <Typography level="body2" sx={{ color: textPrimary }}>{formatDateTime(s.created_at)}</Typography>
                                  </Grid>
                                  <Grid xs={12} sm={6}>
                                    <Typography level="body2" sx={{ color: textMuted, fontSize: '0.75rem' }}>{t('customer.quickView.modified')}</Typography>
                                    <Typography level="body2" sx={{ color: textPrimary }}>{formatDateTime(s.modified_at)}</Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </>
                    );
                  })()}
                </ModalDialog>
              </Modal>
            </>
          )}

          <Modal open={!!receiptPdfPayment} onClose={() => setReceiptPdfPayment(null)}>
            <ModalDialog
              sx={{
                maxWidth: 960,
                width: '100%',
                maxHeight: '95vh',
                overflow: 'auto',
                p: 2,
                borderRadius: cardRadius,
              }}
            >
              <ModalClose />
              <DialogTitle sx={{ fontWeight: 700 }}>{t('paymentsScreen.receiptModalTitle')}</DialogTitle>
              <DialogContent sx={{ px: 0 }}>
                {receiptPdfPayment ? <PaymentReceipt paymentData={receiptPdfPayment} /> : null}
              </DialogContent>
            </ModalDialog>
          </Modal>
        </Container>
      </Box>

      <Snackbar
        autoHideDuration={5000}
        open={!!(exportSuccessMessage || exportError)}
        variant="soft"
        color={exportError ? 'danger' : 'success'}
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setExportSuccessMessage(null);
          setExportError(null);
        }}
      >
        {exportError || exportSuccessMessage}
      </Snackbar>
    </Box>
  );
}
